import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Gavel,
  UserPlus,
  FilePlus2,
  Upload,
  Send,
  Layers,
  Video,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

export const Route = createFileRoute("/education/court-online")({
  head: () => ({
    meta: [
      {
        title:
          "Court Online Training — File, Upload & Litigate Digitally (South Africa) | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Step-by-step training on the South African Office of the Chief Justice (OCJ) Court Online platform: registration, case initiation, document upload, e-filing, service and CaseLines.",
      },
      { property: "og:title", content: "Court Online Training" },
      {
        property: "og:description",
        content:
          "Practical, lesson-by-lesson Court Online training for attorneys, candidate attorneys and law students.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://nkadimengdannylcs.com/education/court-online" },
    ],
  }),
  component: CourtOnlinePage,
});

const LESSONS = [
  {
    icon: UserPlus,
    title: "1. Registering as a Court Online user",
    minutes: 10,
    body: "Create your OCJ Court Online profile, link your law-firm details, capture your practitioner certificate (or student/candidate-attorney status) and verify your email.",
    steps: [
      "Go to courtonline.judiciary.org.za and click Register.",
      "Choose your role: Legal Practitioner, Candidate Attorney, State Attorney, or Litigant in Person.",
      "Capture your ID/passport, practice number (LPC), firm details and contact information.",
      "Upload supporting documents (certificate of enrolment, FICA, proof of address) where required.",
      "Verify the activation email and set a strong password with 2FA.",
    ],
    tips: [
      "Use your firm email address, not Gmail — uploads are linked to that domain in audit logs.",
      "Keep your LPC practice number handy; the system validates it against the LPC register.",
    ],
  },
  {
    icon: FilePlus2,
    title: "2. Initiating a new case",
    minutes: 12,
    body: "Open a new matter under the correct Division (Gauteng, Western Cape, etc.), select case type (motion, action, urgent, opposed/unopposed) and capture parties.",
    steps: [
      "Click New Case > select Division and Court (e.g. Gauteng Division, Pretoria).",
      "Pick Case Type: Action, Application, Motion, Urgent, Interlocutory, Appeal or Review.",
      "Add Plaintiff/Applicant and Defendant/Respondent — each with full citation and address for service.",
      "Capture the legal representatives for each party (your firm by default for the initiating side).",
      "Save Draft → the system issues a draft case number; only on payment of the filing fee does it become a live case number.",
    ],
    tips: [
      "Spell party names exactly as they appear on the summons/notice of motion — mismatches block service.",
      "Use the urgent toggle ONLY when you actually have a certificate of urgency; misuse is flagged.",
    ],
  },
  {
    icon: Upload,
    title: "3. Uploading & indexing documents (CaseLines)",
    minutes: 15,
    body: "Court Online uses CaseLines for the digital case file. Every pleading, affidavit, annexure and notice must be uploaded as a properly named, OCR-searchable PDF and placed in the right bundle section.",
    steps: [
      "Convert each document to PDF/A, OCR it, and confirm file size is under 50 MB per document.",
      "Name files using the convention: PartyRole_DocType_Date.pdf (e.g. Applicant_FoundingAffidavit_2026-03-12.pdf).",
      "Open the matter > CaseLines > Upload > drag files into the correct section (Pleadings, Notices, Affidavits, Annexures, Heads of Argument, Bundle of Authorities).",
      "Re-order pages and split/merge where needed; CaseLines auto-numbers every page (e.g. 003-12).",
      "Click Index & Paginate to lock the bundle for the next step.",
    ],
    tips: [
      "Never upload scanned image-only PDFs — judges cannot search them. OCR is non-negotiable.",
      "Annexures go INSIDE the affidavit PDF, not as separate files (unless > 50 MB).",
    ],
  },
  {
    icon: Send,
    title: "4. E-filing, payment of fees & service",
    minutes: 10,
    body: "Submit the indexed bundle for filing, pay the court fee electronically, and effect service on the other side through Court Online's service module.",
    steps: [
      "From the case dashboard click File Documents > select the documents to file.",
      "Pay the filing fee via EFT or card — Court Online generates a sheriff/court receipt automatically.",
      "Use Service > Add Recipients to serve opposing attorneys; the system records date, time and IP of delivery.",
      "Download the stamped, court-issued copy (with case number and filing date) for your own records.",
      "If serving a self-represented respondent, you still need the sheriff — Court Online lets you generate the sheriff instruction.",
    ],
    tips: [
      "Always download the stamped copy immediately; the court's clock for opposition starts from the stamp date.",
      "Keep proof of payment together with the stamped pleading in your physical/electronic file.",
    ],
  },
  {
    icon: Layers,
    title: "5. CaseLines navigation for hearings",
    minutes: 12,
    body: "At the hearing, judges and parties navigate the bundle by page reference (e.g. 003-17). You must be fluent in jumping, highlighting and presenting documents live.",
    steps: [
      "Open CaseLines > Review Bundle on your laptop and a second screen if available.",
      "Use the Go To Page box (top right) to jump to any section-page reference instantly.",
      "Highlight key paragraphs in advance using the Notes feature — these are private to your team unless shared.",
      "Use Present mode in virtual hearings to share a focused view with the judge and opposing counsel.",
      "Bookmark your heads of argument paragraph references for one-click navigation during oral argument.",
    ],
    tips: [
      "Practice jumping to 5–6 references in under 30 seconds — judges expect speed.",
      "If the judge says 'take me to 008-23', repeat the reference out loud before jumping; it confirms you heard correctly.",
    ],
  },
  {
    icon: Video,
    title: "6. Virtual hearings & post-judgment steps",
    minutes: 10,
    body: "Most opposed and unopposed motions now run on MS Teams via Court Online. Learn how to join, present, get the order uploaded, and apply for leave to appeal digitally.",
    steps: [
      "Check the daily roll on Court Online the day before — the Teams link is on the matter page.",
      "Join 10 minutes early in business attire; mute on entry; rename yourself: Surname (Applicant/Respondent).",
      "Use Present + CaseLines side-by-side; never share your full desktop (it leaks notifications).",
      "After judgment, the registrar uploads the signed order — download it immediately for service.",
      "Applications for leave to appeal, taxation and rescission all run through the same Court Online matter.",
    ],
    tips: [
      "Test your microphone, webcam and Teams link the day before — judges do not wait for tech issues.",
      "Save the signed order to your matter folder AND email it to your client within an hour.",
    ],
  },
] as const;

function CourtOnlinePage() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-navy/5 to-transparent">
        <div className="container-narrow py-14 md:py-20">
          <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-navy/70">
            <Gavel className="h-4 w-4" /> Education Division · Court Online Training
          </div>
          <h1 className="mt-4 font-display text-3xl text-navy md:text-5xl">
            Court Online Training
          </h1>
          <p className="mt-4 max-w-2xl text-base text-foreground/80 md:text-lg">
            Practical, lesson-by-lesson training on the Office of the Chief Justice's
            Court Online platform — registration, case initiation, document upload,
            CaseLines, e-filing, service and virtual hearings. Built for South African
            attorneys, candidate attorneys and final-year LLB students.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="#lesson-1"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90"
            >
              Start the training <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="https://courtonline.judiciary.org.za"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-navy hover:border-navy"
            >
              Open Court Online <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            This is educational training, not legal advice. Always follow the latest
            practice directives of your Division.
          </p>
        </div>
      </section>

      {/* Lesson cards */}
      <section className="container-narrow py-14">
        <h2 className="font-display text-2xl text-navy md:text-3xl">
          Six lessons · about 70 minutes total
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Each lesson covers a real task you will perform on Court Online, with the
          exact clicks, naming conventions and pitfalls to avoid.
        </p>

        <ol className="mt-8 space-y-6">
          {LESSONS.map((l, i) => (
            <li
              key={l.title}
              id={`lesson-${i + 1}`}
              className="scroll-mt-24 rounded-xl border border-border bg-card p-6"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-navy/5 text-navy">
                  <l.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg text-navy md:text-xl">
                  {l.title}
                </h3>
                <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  ~{l.minutes} min
                </span>
              </div>

              <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                {l.body}
              </p>

              <div className="mt-5 grid gap-5 md:grid-cols-2">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-navy/70">
                    Step-by-step
                  </h4>
                  <ol className="mt-2 space-y-2 text-sm text-foreground/80">
                    {l.steps.map((s, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy/10 text-[11px] font-semibold text-navy">
                          {idx + 1}
                        </span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-navy/70">
                    Practical tips
                  </h4>
                  <ul className="mt-2 space-y-2 text-sm text-foreground/80">
                    {l.tips.map((t, idx) => (
                      <li key={idx} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Where next */}
      <section className="border-t border-border bg-muted/30">
        <div className="container-narrow py-12">
          <h2 className="font-display text-2xl text-navy">Continue your training</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Court Online is one piece of digital litigation. Strengthen the rest of your
            workflow with our Writing and Research programmes.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Link
              to="/education/writing"
              className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-navy"
            >
              <h3 className="font-display text-navy">Legal Writing Academy</h3>
              <p className="mt-2 text-sm text-foreground/75">
                Heads of argument, founding and answering affidavits, and pleadings —
                with rubric-graded feedback.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                Open writing academy <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link
              to="/education/research"
              className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-navy"
            >
              <h3 className="font-display text-navy">Legal Research Hub</h3>
              <p className="mt-2 text-sm text-foreground/75">
                IRAC generator, case-analysis tool, judgment summariser and footnote
                generator.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                Open research hub <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link
              to="/resources"
              className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-navy"
            >
              <h3 className="font-display text-navy">Resource Library</h3>
              <p className="mt-2 text-sm text-foreground/75">
                Templates for notices, indexes, heads of argument and CaseLines bundle
                checklists.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                Browse the library <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
